import React, { useEffect, useMemo, useState } from 'react';
import { Bell, CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  markNotificationRead,
  type MasterAdminNotificationRow,
} from '../../services/MasterAdmin/masterAdminNotificationService';
import {
  formatNotificationBody,
  getNotificationLink,
  getNotificationServiceLabel,
} from '../../utils/masterAdminNotifications';

type ReadFilter = 'all' | 'unread';

const PAGE_SIZE = 25;

const SERVICE_OPTIONS = [
  { value: 'ambulance', label: 'Ambulance' },
  { value: 'medicine', label: 'Medicine' },
  { value: 'grievance', label: 'Grievance' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'blood_bank', label: 'Blood bank' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'diagnostics', label: 'Diagnostics' },
  { value: 'doctor_clinic', label: 'Doctor clinic' },
  { value: 'doctor_blogs', label: 'Doctor blogs' },
  { value: 'orders_payments', label: 'Orders and payments' },
];

const SERVICE_TYPE_PATTERNS: Record<string, string[]> = {
  ambulance: ['AMBULANCE'],
  medicine: ['MEDICINE', 'PRESCRIPTION'],
  grievance: ['GRIEVANCE'],
  vendor: ['VENDOR'],
  blood_bank: ['BLOOD'],
  hospital: ['HOSPITAL'],
  diagnostics: ['LAB', 'DIAGNOSTIC'],
  doctor_clinic: ['DOCTOR', 'CLINIC'],
  doctor_blogs: ['DOCTOR_BLOG'],
  orders_payments: ['ORDER', 'PAYMENT'],
};

function matchesServiceFilter(type: string, service: string): boolean {
  if (service === 'all') return true;
  const patterns = SERVICE_TYPE_PATTERNS[service];
  if (!patterns) return true;
  const normalizedType = type.toUpperCase();
  return patterns.some((pattern) => normalizedType.includes(pattern));
}

type NotificationsPageProps = {
  /** Writer feed shows only writer-targeted notifications (API already role-filters). */
  audience?: 'master' | 'writer';
};

const NotificationsPage: React.FC<NotificationsPageProps> = ({ audience = 'master' }) => {
  const navigate = useNavigate();
  const isWriter = audience === 'writer';
  const [notifications, setNotifications] = useState<MasterAdminNotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [serviceFilter, setServiceFilter] = useState(isWriter ? 'doctor_blogs' : 'all');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchNotifications({
          page,
          limit: PAGE_SIZE,
          unreadOnly: readFilter === 'unread',
          service: isWriter ? 'all' : serviceFilter,
        });
        if (!mounted) return;
        setNotifications(response.data.notifications);
        setTotalPages(response.data.pagination.totalPages || 1);
        setTotalItems(response.data.pagination.totalItems || 0);
      } catch (err) {
        if (!mounted) return;
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications. Please try again later.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [page, readFilter, serviceFilter, isWriter]);

  useEffect(() => {
    setPage(1);
  }, [readFilter, serviceFilter]);

  const visibleNotifications = useMemo(
    () =>
      notifications
        .filter((notification) =>
          isWriter ? true : matchesServiceFilter(notification.type, serviceFilter)
        )
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [notifications, serviceFilter, isWriter]
  );

  const handleOpenNotification = async (notification: MasterAdminNotificationRow) => {
    if (!notification.isRead) {
      try {
        await markNotificationRead(notification.id);
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notification.id
              ? { ...item, isRead: true, readAt: new Date().toISOString() }
              : item
          )
        );
      } catch {
        /* keep navigation available even if read marking fails */
      }
    }

    const href = getNotificationLink(notification);
    if (href) {
      navigate(href);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-5">
      <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800 dark:shadow-gray-900/30 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isWriter ? 'Writer notifications' : 'All notifications'}
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {isWriter
                ? 'Doctor blog submissions and other writer-targeted alerts.'
                : 'Showing notifications from the admin notification feed.'}
            </p>
          </div>

          <div className={`grid grid-cols-1 gap-3 ${isWriter ? 'sm:grid-cols-1 lg:w-[16rem]' : 'sm:grid-cols-2 lg:w-[26rem]'}`}>
            <select
              value={readFilter}
              onChange={(e) => setReadFilter(e.target.value as ReadFilter)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All notifications</option>
              <option value="unread">Unread only</option>
            </select>
            {!isWriter && (
              <select
                value={serviceFilter}
                onChange={(e) => setServiceFilter(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All services</option>
                {SERVICE_OPTIONS.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800 dark:shadow-gray-900/30">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 px-4 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
          </div>
        ) : error ? (
          <div className="px-4 py-12 text-center text-sm text-red-600 dark:text-red-400">{error}</div>
        ) : visibleNotifications.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No notifications found for the selected filters.
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {visibleNotifications.map((notification) => {
              const link = getNotificationLink(notification);
              return (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => handleOpenNotification(notification)}
                  className={`w-full px-4 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/70 sm:px-5 ${
                    !notification.isRead ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                          {notification.title}
                        </h3>
                        {!notification.isRead && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                        {formatNotificationBody(notification)}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium uppercase tracking-wide text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {notification.type}
                        </span>
                        <span>{getNotificationServiceLabel(notification.type)}</span>
                        {notification.isRead && (
                          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Read
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-left sm:text-right">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(notification.createdAt).toLocaleString()}
                      </p>
                      {link && (
                        <span className="mt-2 inline-block text-xs font-medium text-primary-600 dark:text-primary-400">
                          Open detail -&gt;
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page <span className="font-medium">{page}</span> of{' '}
              <span className="font-medium">{totalPages}</span>
              <span className="ml-2">({totalItems} total)</span>
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                disabled={page >= totalPages}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
